import React, { createContext, useContext } from 'react';
import { Box } from '@mui/material';

// Lets a {{slot}} reference deep inside a content diff jump to and expand
// that slot node's own row, without prop-drilling through every diff component.
export const DiffNavContext = createContext({ slotMap: new Map(), jumpToNode: () => {} });

const SLOT_REF_RE = /\{\{(\w+)\}\}/g;
const SLOT_REF_COLOR = '#7c3aed';

// Always plain purple text -- no background pill. A pill would sit on top of
// the surrounding red/green add-remove highlight and hide it, making it
// impossible to tell whether the reference itself was added, removed, or
// unchanged just by looking at it.
function SlotRefLink({ name }) {
  const { slotMap, jumpToNode } = useContext(DiffNavContext);
  const targetId = slotMap.get(name);
  const sx = { color: SLOT_REF_COLOR, fontWeight: 500 };
  if (!targetId) return <Box component="span" sx={sx}>{`{{${name}}}`}</Box>;
  return (
    <Box
      component="span"
      role="link"
      tabIndex={0}
      onClick={() => jumpToNode(targetId)}
      onKeyDown={(e) => { if (e.key === 'Enter') jumpToNode(targetId); }}
      sx={{ ...sx, cursor: 'pointer', textDecoration: 'underline', textDecorationStyle: 'dotted', textDecorationColor: SLOT_REF_COLOR }}
    >
      {`{{${name}}}`}
    </Box>
  );
}

// Renders text with {{slot}} references as jump links to that slot node's row.
export function renderDiffText(text) {
  if (!text) return null;
  const parts = [];
  let lastIndex = 0;
  let match;
  let key = 0;
  SLOT_REF_RE.lastIndex = 0;
  while ((match = SLOT_REF_RE.exec(text))) {
    if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));
    parts.push(<SlotRefLink key={`slot-${key++}`} name={match[1]} />);
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts;
}
